import ContinuationNote from '../Ladder/ContinuationNote';
/**
 * DiagnoseTrace — rich rendering of an ExplanationEnvelope inside a chat
 * turn (#139, epic #127). The analyst asks "why did first response time
 * drop?" in plain language; this renders the answer: verdict, an
 * auto-layout Sankey of the causal chain (computed from chain depths and
 * |z|-shares — never hand-tuned), segment attribution, matched events,
 * ruled-out hypotheses, and the budget footer.
 *
 * Layout approximation is labeled: ribbon widths are normalized |z|·conf
 * shares per level, not true contribution decomposition (that lands with
 * the P1 backtest work).
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, Collapse, Paper, Stack, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const VERDICT_SEVERITY: Record<string, 'success' | 'info' | 'warning' | 'error'> = {
  explained: 'success',
  corrected: 'success',
  within_normal: 'info',
  data_late: 'warning',
  immature: 'info',
  insufficient_data: 'warning',
  unexplained: 'warning',
  error: 'error',
};

const STATUS_COLOR: Record<string, string> = {
  exact: 'var(--accent)', confirmed: 'var(--ok)', proposed: 'var(--warn)', contested: 'var(--bad)',
};

interface ChainNode {
  metric: string;
  parent: string;
  delta_pct: number | null;
  z: number;
  edge: { status: string; kind: string; confidence: number | null; provenance?: any };
  depth: number;
}

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
}

/**
 * Deterministic layered Sankey. Causes flow left → the target at the
 * right. Column = maxDepth − depth; each node's band height is its
 * |z|·confidence share of its parent's band, leaving an "unexplained"
 * gap when shares don't sum to the parent.
 */
function SankeySvg({ target, chain }: { target: string; chain: ChainNode[] }) {
  const layout = useMemo(() => {
    const maxDepth = Math.max(...chain.map((c) => c.depth));
    const H = 190;
    const W = 640;
    const NODE_W = 10;
    const colX = (depth: number) =>
      40 + ((maxDepth - depth) / maxDepth) * (W - 140);

    type Band = { node: ChainNode | null; id: string; x: number; y: number; h: number;
                  pOff: number };
    const bands = new Map<string, Band>();
    bands.set(target, { node: null, id: target, x: W - 80, y: 30, h: H - 60, pOff: 0 });

    // children of each parent, deterministic order
    const kids = new Map<string, ChainNode[]>();
    chain.forEach((c) => {
      const arr = kids.get(c.parent) || [];
      arr.push(c);
      kids.set(c.parent, arr);
    });
    kids.forEach((arr) => arr.sort((a, b) =>
      Math.abs(b.z) * (b.edge.confidence ?? 0.5) - Math.abs(a.z) * (a.edge.confidence ?? 0.5)
      || a.metric.localeCompare(b.metric)));

    const walk = (parentId: string) => {
      const parent = bands.get(parentId);
      const children = kids.get(parentId) || [];
      if (!parent || !children.length) return;
      const weights = children.map((c) => Math.abs(c.z) * (c.edge.confidence ?? 0.5));
      const total = weights.reduce((a, b) => a + b, 0) || 1;
      // children fill at most 85% of the parent band — the remainder is
      // honest unexplained space
      let y = parent.y + parent.h * 0.05;
      let pOff = parent.h * 0.05; // cumulative inflow offset on the parent —
                                  // siblings occupy distinct parent segments
      children.forEach((c, i) => {
        const h = Math.max(10, parent.h * 0.85 * (weights[i] / total));
        bands.set(c.metric, { node: c, id: c.metric, x: colX(c.depth), y, h, pOff });
        y += h + 6;
        pOff += h;
        walk(c.metric);
      });
    };
    walk(target);
    return { bands: Array.from(bands.values()), W, H, NODE_W };
  }, [target, chain]);

  const { bands, W, H, NODE_W } = layout;
  const byId = new Map(bands.map((b) => [b.id, b]));

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H + 30}`}
        style={{ display: 'block', width: '100%', minWidth: 520 }}
        role="img"
        aria-label={`Causal trace: ${chain.length} drivers flowing into ${target}`}
      >
        {bands.filter((b) => b.node).map((b) => {
          const parent = byId.get(b.node!.parent);
          if (!parent) return null;
          const color = STATUS_COLOR[b.node!.edge.status] ?? 'var(--accent)';
          const x0 = b.x + NODE_W;
          const x1 = parent.x;
          const cx = (x0 + x1) / 2;
          const py = parent.y + Math.min(b.pOff, parent.h - b.h);
          const d = `M${x0},${b.y} C${cx},${b.y} ${cx},${py} ${x1},${py}` +
            ` L${x1},${py + b.h} C${cx},${py + b.h} ${cx},${b.y + b.h} ${x0},${b.y + b.h} Z`;
          const dashed = b.node!.edge.status === 'proposed' || b.node!.edge.status === 'contested';
          return (
            <path
              key={`r-${b.id}`} d={d} fill={color} fillOpacity={0.28}
              stroke={dashed ? color : 'none'} strokeDasharray={dashed ? '5 4' : undefined}
              strokeOpacity={0.8}
            />
          );
        })}
        {bands.map((b) => {
          const color = b.node ? (STATUS_COLOR[b.node.edge.status] ?? 'var(--accent)') : 'var(--accent)';
          const label = b.id.split('.').pop() || b.id;
          return (
            <g key={`n-${b.id}`}>
              <rect x={b.x} y={b.y} width={NODE_W} height={b.h} rx={3} fill={color} />
              <text
                x={b.x + NODE_W / 2} y={b.y - 6} textAnchor="middle"
                style={{ font: '600 10px ui-monospace, Menlo, monospace', fill: 'currentColor' }}
              >
                {label}
              </text>
              <text
                x={b.x + NODE_W / 2} y={b.y + b.h + 12} textAnchor="middle"
                style={{ font: '700 9.5px ui-monospace, Menlo, monospace', fill: 'currentColor', opacity: 0.75 }}
              >
                {b.node ? fmtPct(b.node.delta_pct) : ''}
              </text>
            </g>
          );
        })}
      </svg>
      <Typography variant="caption" color="text.disabled">
        ribbon width = normalized |z|·confidence share per level (approximation —
        true contribution math lands with backtesting) · dashed = proposed edge
      </Typography>
    </Box>
  );
}

/** A link colleagues click from a shared trace: http(s) only, never a scheme a browser would run. */
export function isSafeLink(url: unknown): url is string {
  if (typeof url !== 'string' || url.length > 2000) return false;
  const u = url.trim().toLowerCase();
  return u.startsWith('https://') || u.startsWith('http://');
}

export default function DiagnoseTrace({ envelope }: { envelope: any }) {
  const [showDetail, setShowDetail] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  if (!envelope?.verdict) return null;
  const verdict = envelope.verdict;
  const chain: ChainNode[] = verdict.kind === 'immature' ? [] : envelope.chain || [];
  const layers: any[] = envelope.verdict_layers || [];

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {layers.length > 1 && (
        // Corrections layer (#186): the walk's own verdict, then each correction with who and when, then the current.
        <Stack spacing={0.75} data-testid="verdict-layers">
          {layers.map((l, i) => (
            <Box key={l.id || i} data-testid="verdict-layer" data-layer={l.layer}
                 sx={{ pl: 1.25, borderLeft: 3, borderColor: i === layers.length - 1 ? 'success.main' : 'divider', opacity: i === layers.length - 1 ? 1 : 0.75 }}>
              <Typography variant="overline" sx={{ letterSpacing: 1 }}>
                {l.layer === 'original' ? 'original verdict' : `correction · ${l.asserted_by_name || l.asserted_by || 'someone'} · ${String(l.asserted_at || '').slice(0, 10)}`}
                {i === layers.length - 1 ? ' · current' : ''}
              </Typography>
              <Typography variant="body2">{l.verdict?.headline}</Typography>
              {l.layer === 'correction' && isSafeLink(l.evidence_url) && (
                <Typography variant="caption"><a href={l.evidence_url} target="_blank" rel="noreferrer noopener">evidence</a></Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
      <Alert severity={VERDICT_SEVERITY[verdict.kind] ?? 'info'} icon={false}>
        <Typography variant="overline" sx={{ letterSpacing: 1 }}>
          {String(verdict.kind).replace('_', ' ')} · confidence {verdict.confidence}
        </Typography>
        <Typography sx={{ fontWeight: 600, mt: 0.5 }}>{verdict.headline}</Typography>
        {verdict.caveat && (
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            ⚠ {verdict.caveat}
          </Typography>
        )}
      </Alert>
      <ContinuationNote continuation={envelope.continuation} />

      {chain.length >= 2 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <SankeySvg target={envelope.target.metric} chain={chain} />
        </Paper>
      ) : chain.length === 1 ? (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {envelope.target.metric} ← {chain[0].metric} {fmtPct(chain[0].delta_pct)}{' '}
          <Chip size="small" variant="outlined" label={chain[0].edge.status} sx={{ height: 18, fontSize: 10 }} />
        </Typography>
      ) : null}

      {verdict.kind !== 'immature' && envelope.segments?.segments?.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Concentrated in <code>{envelope.segments.dimension}=
          {String(envelope.segments.segments[0].segment)}</code>{' '}
          ({Math.round(envelope.segments.top_share * 100)}% of summed segment deltas).
        </Typography>
      )}

      {envelope.annotations?.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <BoltIcon fontSize="small" color="warning" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {envelope.annotations[0].title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {String(envelope.annotations[0].ts).slice(0, 10)} (rollout time) ·{' '}
              {envelope.annotations[0].source}
              {/* the evidence link rides in provenance (an MCP note's source_ref is its claim key); older rows kept it as source_ref */}
              {((envelope.annotations[0] as any).provenance?.evidence_url || envelope.annotations[0].source_ref)
                ? ` · ${(envelope.annotations[0] as any).provenance?.evidence_url || envelope.annotations[0].source_ref}` : ''}
            </Typography>
          </Box>
        </Stack>
      )}

      <Box>
        <Box
          role="button" tabIndex={0} aria-expanded={showDetail}
          onClick={() => setShowDetail(!showDetail)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowDetail(!showDetail); }
          }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                color: 'text.secondary', '&:focus-visible': { outline: 2, outlineColor: 'primary.main' } }}
        >
          <ExpandMoreIcon
            fontSize="small"
            sx={{ transform: showDetail ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }}
          />
          <Typography variant="caption">evidence & alternatives</Typography>
        </Box>
        <Collapse in={showDetail}>
          <Stack spacing={0.5} sx={{ mt: 1, ml: 1 }}>
            {(envelope.alternatives || []).map((alt: any, i: number) => (
              <Typography
                key={i} variant="caption"
                sx={{ textDecoration: alt.status === 'ruled_out' ? 'line-through' : 'none' }}
              >
                {/* struck through = tested and eliminated. An unverified
                    hypothesis was never tested, so it must not look
                    eliminated OR merely open — it gets its own marker. */}
                {alt.status === 'unverified' && (
                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 700, mr: 0.5 }}>
                    ?
                  </Box>
                )}
                <b>{alt.hypothesis}</b> — {alt.reason}
              </Typography>
            ))}
            {chain.map((c) => (
              <Typography key={c.metric} variant="caption" sx={{ fontFamily: 'monospace' }}>
                {c.metric} · z={c.z.toFixed(1)} · {c.edge.status}
                {c.edge.provenance?.evidence ? ` — ${String(c.edge.provenance.evidence)}` : ''}
              </Typography>
            ))}
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
              {envelope.budget?.queries} / {envelope.budget?.limit} queries · effort{' '}
              {envelope.budget?.effort}
            </Typography>
          </Stack>
        </Collapse>
      </Box>

      {/* the walk's real work — every measured metric with its SQL (#trust) */}
      {envelope.activity?.length > 0 && (
        <Box>
          <Box
            role="button" tabIndex={0} aria-expanded={showActivity}
            onClick={() => setShowActivity(!showActivity)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowActivity(!showActivity); }
            }}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                  color: 'text.secondary', '&:focus-visible': { outline: 2, outlineColor: 'primary.main' } }}
          >
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: showActivity ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }}
            />
            <Typography variant="caption">
              trace activity · {envelope.budget?.queries} queries across{' '}
              {envelope.activity.length} measurements · no LLM
            </Typography>
          </Box>
          <Collapse in={showActivity}>
            <Stack spacing={1} sx={{ mt: 1, ml: 1 }}>
              {envelope.activity.map((a: any, i: number) => (
                <Box key={i}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {i + 1}. {a.metric} · {a.windows} window{a.windows === 1 ? '' : 's'}
                    {a.group_by ? ` · by ${a.group_by}` : ''}
                  </Typography>
                  {a.sql && (
                    <Box component="pre" sx={{
                      m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1,
                      fontSize: 10.5, fontFamily: 'monospace', overflowX: 'auto',
                      whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto',
                    }}>
                      {a.sql}
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Stack>
  );
}
